import {
  Content,
  ContentListItem,
  ContentConversationListItem,
  ContentListQueryInput,
  ContentListResult,
  ContentUpdate,
  CreateCalendarEventFromScratchInput,
  DeletionCommitInput,
  DeletionCommitResult,
  DeletionImpact,
  DeletionPreviewInput,
  CreateMessageReplyDraftInput,
  UpdateMessageDraftInput,
} from '@vivport/backend/types';
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { provenanceKeys } from './useProvenanceQueries';
import { calendarKeys } from './useCalendarQueries';
import {
  createCalendarEventFromEventContext,
  createCalendarEventFromScratch,
  createMessageReplyDraft,
  discardMessageDraft,
  bulkDeleteContent,
  deleteContentAfterPreview,
  previewContentDeletion,
  createDocumentChild,
  createDocumentSibling,
  deleteDocumentSubtree,
  createContent,
  executeContentAction,
  getContent,
  getDocumentRender,
  listContent,
  markAsRead,
  moveDocumentNode,
  moveDocumentTableImage,
  moveObservationMedia,
  regenerateContent,
  sendMessageDraft,
  updateMessageDraft,
  setReadState,
  updateCalendarEventFromEventContext,
  toggleStar,
  updateContent,
} from '../api/content-api';

/**
 * Content Query Keys Factory
 * Centralized query key factory for content-related React Query hooks
 */
export const contentKeys = {
  all: ['content'] as const,
  lists: () => [...contentKeys.all, 'list'] as const,
  list: (filters?: ContentListQueryInput) => [...contentKeys.lists(), filters] as const,
  details: () => [...contentKeys.all, 'detail'] as const,
  detail: (id: number) => [...contentKeys.details(), id] as const,
  documentRender: (rootDocumentId: number) =>
    [...contentKeys.all, 'documentRender', rootDocumentId] as const,
  deletionPreview: (roots: number[], optionalIncluded: number[]) =>
    [
      ...contentKeys.all,
      'deletionPreview',
      [...roots].sort((a, b) => a - b),
      [...optionalIncluded].sort((a, b) => a - b),
    ] as const,
};

const invalidateAllDocumentRenders = (queryClient: ReturnType<typeof useQueryClient>): void => {
  queryClient.invalidateQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) &&
      q.queryKey[0] === 'content' &&
      q.queryKey[1] === 'documentRender',
  });
};

const invalidateAllContentDetails = async (queryClient: ReturnType<typeof useQueryClient>): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: contentKeys.details(), refetchType: 'all' });
};

export const isConversationProjectionItem = (
  item: ContentListResult['items'][number]
): item is ContentConversationListItem => {
  return (
    typeof item === 'object' &&
    item !== null &&
    'projectionType' in item &&
    item.projectionType === 'CONVERSATION'
  );
};

type DocumentLikeContent = Content & {
  rootCode?: string | null;
  code: string;
  hierarchyTree?: {
    id: number;
    children?: Array<DocumentLikeContent['hierarchyTree']>;
  };
};

const isDocumentLikeContent = (value: unknown): value is DocumentLikeContent => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<Content>;
  return candidate.contentType === 'DOCUMENT' && typeof candidate.id === 'number';
};

const mergeDetailCachePreservingHierarchy = (
  existing: Content | undefined,
  incoming: Content
): Content => {
  if (
    existing &&
    isDocumentLikeContent(existing) &&
    isDocumentLikeContent(incoming) &&
    existing.hierarchyTree &&
    !incoming.hierarchyTree
  ) {
    return {
      ...incoming,
      hierarchyTree: existing.hierarchyTree
    } as Content;
  }
  return incoming;
};

const resolveRootCode = (document: DocumentLikeContent): string | null => {
  if (typeof document.rootCode === 'string' && document.rootCode.length > 0) {
    return document.rootCode;
  }
  if (typeof document.code === 'string' && document.code.length > 0) {
    return document.code;
  }
  return null;
};

const collectHierarchyDocumentIds = (
  hierarchyTree: DocumentLikeContent['hierarchyTree'] | undefined
): number[] => {
  if (!hierarchyTree) {
    return [];
  }
  const ids: number[] = [];
  const stack = [hierarchyTree];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      break;
    }
    ids.push(current.id);
    const children = Array.isArray(current.children)
      ? current.children.filter(
          (child): child is NonNullable<DocumentLikeContent['hierarchyTree']> => Boolean(child)
        )
      : [];
    for (let index = children.length - 1; index >= 0; index -= 1) {
      stack.push(children[index]);
    }
  }
  return ids;
};

const invalidateDetailQueriesByIds = async (
  queryClient: ReturnType<typeof useQueryClient>,
  ids: number[]
): Promise<void> => {
  const uniqueIds = Array.from(new Set(ids.filter((id): id is number => typeof id === 'number' && id > 0)));
  if (uniqueIds.length === 0) {
    await invalidateAllContentDetails(queryClient);
    return;
  }
  await Promise.all(
    uniqueIds.map((id) =>
      queryClient.invalidateQueries({
        queryKey: contentKeys.detail(id),
        exact: true,
        refetchType: 'all'
      })
    )
  );
};

const invalidateDocumentRootDetailCaches = async (
  queryClient: ReturnType<typeof useQueryClient>,
  rootCode: string | null
): Promise<void> => {
  if (typeof rootCode !== 'string') {
    await invalidateAllContentDetails(queryClient);
    return;
  }

  const detailQueries = queryClient.getQueriesData<Content>({ queryKey: contentKeys.details() });
  const rootScopedKeys = detailQueries
    .filter(([, data]) => {
      if (!isDocumentLikeContent(data)) {
        return false;
      }
      return resolveRootCode(data) === rootCode;
    })
    .map(([queryKey]) => queryKey);

  if (rootScopedKeys.length === 0) {
    await invalidateAllContentDetails(queryClient);
    return;
  }

  await Promise.all(
    rootScopedKeys.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'all' })
    )
  );
};

/**
 * Content List Query Hook
 * Returns IDs only while caching full items in detail queries
 */
export const useContentList = (filters?: ContentListQueryInput) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: contentKeys.list(filters),
    queryFn: async (): Promise<{
      projection: ContentListResult['projection'];
      items: ContentListItem[];
      ids: number[];
      nextCursor: number | null;
      totalItems: number;
      matches: NonNullable<ContentListResult['matches']>;
    }> => {
      const result: ContentListResult = await listContent(filters);

      if (result.projection === 'CONTENT') {
        result.items.forEach((item) => {
          if (isConversationProjectionItem(item)) {
            return;
          }
          if (item.contentType === 'DOCUMENT') {
            // Document list payloads do not include hierarchyTree.
            // Avoid seeding empty/stale document detail cache; keep existing rich cache only.
            queryClient.setQueryData<Content | undefined>(contentKeys.detail(item.id), (existing) => {
              if (!existing) {
                return existing;
              }
              return mergeDetailCachePreservingHierarchy(existing, item);
            });
            return;
          }

          queryClient.setQueryData<Content>(contentKeys.detail(item.id), (existing) =>
            mergeDetailCachePreservingHierarchy(existing, item)
          );
        });
      }

      if (result.projection === 'CONVERSATION') {
        result.items.forEach((item) => {
          if (!isConversationProjectionItem(item)) {
            return;
          }
          queryClient.setQueryData<Content>(contentKeys.detail(item.representativeMessageId), (existing) =>
            mergeDetailCachePreservingHierarchy(existing, item.representativeMessage)
          );
        });
      }

      return {
        projection: result.projection,
        items: result.items,
        ids: result.items.map((item) =>
          isConversationProjectionItem(item) ? item.representativeMessageId : item.id
        ),
        nextCursor: result.nextCursor,
        totalItems: result.totalItems,
        matches: result.matches ?? {},
      };
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Content Detail Query Hook
 * Pre-populated by list queries, fetches individual items by ID
 */
export const useContentDetail = (
  id: number,
  options?: Omit<UseQueryOptions<Content, Error>, 'queryKey' | 'queryFn' | 'enabled'> & { enabled?: boolean }
) => {
  return useQuery({
    queryKey: contentKeys.detail(id),
    queryFn: () => getContent(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
    enabled: id > 0 && (options?.enabled ?? true),
  });
};

/**
 * Content Create Mutation Hook
 * Caches new item in detail and invalidates all lists
 */
export const useContentCreate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createContent,
    onSuccess: (newContent) => {
      // Cache the new item in detail query
      queryClient.setQueryData(contentKeys.detail(newContent.id), newContent);

      // Invalidate all list queries to refetch with new item
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
      const ref = typeof newContent.code === 'string' && newContent.code.length > 0
        ? newContent.code
        : null;
      if (ref) {
        queryClient.invalidateQueries({ queryKey: provenanceKeys.listFor(ref) });
      } else {
        queryClient.invalidateQueries({ queryKey: provenanceKeys.lists() });
      }
    },
  });
};

/**
 * Observation Media Move Mutation Hook
 * Relocates one captured item into another Observation. The chat's own history is a
 * reducer, not a query, so the live pane is refreshed by its caller — this invalidates
 * the NeoChat conversation query that the details page reads.
 */
export const useObservationMediaMove = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: moveObservationMedia,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['neoChatConversation', result.apexContentId] });
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(result.contentId) });
      queryClient.invalidateQueries({ queryKey: provenanceKeys.lists() });
    },
  });
};

/**
 * Content Update Mutation Hook
 * Updates detail cache only (lists auto-update via shared cache)
 */
export const useContentUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ContentUpdate }) =>
      updateContent(id, data),
    onSuccess: (updatedContent) => {
      queryClient.setQueryData(contentKeys.detail(updatedContent.id), updatedContent);
      const ref = typeof updatedContent.code === 'string' && updatedContent.code.length > 0
        ? updatedContent.code
        : String(updatedContent.id);
      queryClient.setQueryData(['content-ref', ref], updatedContent);
      invalidateAllDocumentRenders(queryClient);
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
      const provenanceRef = typeof updatedContent.code === 'string' && updatedContent.code.length > 0
        ? updatedContent.code
        : null;
      if (provenanceRef) {
        queryClient.invalidateQueries({ queryKey: provenanceKeys.listFor(provenanceRef) });
      } else {
        queryClient.invalidateQueries({ queryKey: provenanceKeys.lists() });
      }
    },
  });
};

/**
 * Document Render Query Hook
 * Returns the entire document subtree (sections + per-section citations + attachment summary).
 */
export const useDocumentRender = (rootDocumentId: number, opts?: { enabled?: boolean }) =>
  useQuery({
    queryKey: contentKeys.documentRender(rootDocumentId),
    queryFn: () => getDocumentRender(rootDocumentId),
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: rootDocumentId > 0 && (opts?.enabled ?? true),
  });

/**
 * Content Regenerate Mutation Hook
 * Invalidates detail cache to refetch regenerated content
 */
export const useContentRegenerate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, awaitLLM = true }: { id: number; awaitLLM?: boolean }) =>
      regenerateContent(id, awaitLLM),
    onSuccess: async (_, { id }) => {
      // Detail routes use `['content-ref', codeOrId]`; other surfaces use `contentKeys.detail(id)`.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.detail(id) }),
        queryClient.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'content-ref',
        }),
      ]);
      invalidateAllDocumentRenders(queryClient);
    },
  });
};

/**
 * Content Bulk Delete Mutation Hook
 * Removes multiple detail caches and invalidates lists
 */
/**
 * Deletion Preview Query Hook
 * Enabled-gated so it fires only while the caller (the delete dialog) wants it.
 */
export const useContentDeletionPreview = (
  input: DeletionPreviewInput,
  options?: Omit<UseQueryOptions<DeletionImpact, Error>, 'queryKey' | 'queryFn' | 'enabled'> & { enabled?: boolean }
) => {
  const optionalIncluded = input.optionalIncluded ?? [];
  return useQuery({
    queryKey: contentKeys.deletionPreview(input.roots, optionalIncluded),
    queryFn: () => previewContentDeletion(input),
    // The graph is live and the member is about to destroy part of it, so a cached
    // answer is the wrong answer. Previous data is held only to stop the tree
    // flickering while a checkbox toggle re-fetches.
    staleTime: 0,
    placeholderData: (previous) => previous,
    ...options,
    enabled: input.roots.length > 0 && (options?.enabled ?? true),
  });
};

// Shared by both delete paths so their cache handling cannot drift apart.
function applyDeletionResultToCache(
  queryClient: ReturnType<typeof useQueryClient>,
  result: DeletionCommitResult
): void {
  for (const contentId of result.deletedContentIds) {
    queryClient.removeQueries({ queryKey: contentKeys.detail(contentId) });
  }
  queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
  // Provenance keys are string-addressed (contentRef / provenanceCode) and a
  // reaped container is known here only by numeric id, so the whole namespace is
  // invalidated. Reaping is rare; a broader refetch beats a per-key
  // invalidation that cannot be built from the data on hand.
  if (result.reapedContainerIds.length > 0) {
    queryClient.invalidateQueries({ queryKey: provenanceKeys.all });
  }
}

/**
 * Commit a deletion plan the member has already reviewed. Takes the planHash the
 * preview returned, so the server refuses the delete if the graph moved since.
 */
export const useContentDeletionCommit = () => {
  const queryClient = useQueryClient();

  return useMutation<DeletionCommitResult, Error, DeletionCommitInput>({
    mutationFn: bulkDeleteContent,
    onSuccess: (result) => applyDeletionResultToCache(queryClient, result),
  });
};

export const useContentBulkDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteContentAfterPreview,
    onSuccess: (result) => applyDeletionResultToCache(queryClient, result),
  });
};
export const useCreateDocumentChild = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDocumentChild,
    onSuccess: async (result) => {
      queryClient.setQueryData(contentKeys.detail(result.document.id), result.document);
      const hierarchyIds = collectHierarchyDocumentIds(
        (result.document as DocumentLikeContent).hierarchyTree
      );
      if (hierarchyIds.length > 0) {
        await invalidateDetailQueriesByIds(queryClient, hierarchyIds);
      } else {
        await invalidateDocumentRootDetailCaches(
          queryClient,
          resolveRootCode(result.document as DocumentLikeContent)
        );
      }
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
      invalidateAllDocumentRenders(queryClient);
    },
  });
};

export const useCreateDocumentSibling = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDocumentSibling,
    onSuccess: async (result) => {
      queryClient.setQueryData(contentKeys.detail(result.document.id), result.document);
      const hierarchyIds = collectHierarchyDocumentIds(
        (result.document as DocumentLikeContent).hierarchyTree
      );
      if (hierarchyIds.length > 0) {
        await invalidateDetailQueriesByIds(queryClient, hierarchyIds);
      } else {
        await invalidateDocumentRootDetailCaches(
          queryClient,
          resolveRootCode(result.document as DocumentLikeContent)
        );
      }
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
      invalidateAllDocumentRenders(queryClient);
    },
  });
};

export const useMoveDocumentNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: moveDocumentNode,
    onSuccess: async (result) => {
      queryClient.setQueryData(contentKeys.detail(result.document.id), result.document);
      await invalidateDocumentRootDetailCaches(
        queryClient,
        resolveRootCode(result.document as DocumentLikeContent)
      );
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
      invalidateAllDocumentRenders(queryClient);
    },
  });
};

export const useMoveDocumentTableImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: moveDocumentTableImage,
    onSuccess: async (document) => {
      queryClient.setQueryData(contentKeys.detail(document.id), document);
      await invalidateDocumentRootDetailCaches(
        queryClient,
        resolveRootCode(document as DocumentLikeContent)
      );
      invalidateAllDocumentRenders(queryClient);
    },
  });
};

export const useDeleteDocumentSubtree = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDocumentSubtree,
    onSuccess: async () => {
      await invalidateDocumentRootDetailCaches(queryClient, null);
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
      invalidateAllDocumentRenders(queryClient);
    },
  });
};

/**
 * Content Action Mutation Hook
 * Generic hook for executing content-specific actions
 */
export const useContentAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      id, 
      actionName, 
      params 
    }: { 
      id: number; 
      actionName: string; 
      params?: Record<string, unknown> 
    }) => executeContentAction(id, actionName, params),
    onSuccess: (_, { id }) => {
      // Invalidate detail cache to refetch updated content
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(id) });
    },
  });
};

export const useCreateCalendarEventFromScratch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCalendarEventFromScratchInput) => createCalendarEventFromScratch(input),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: contentKeys.detail(result.contentId) }),
        queryClient.invalidateQueries({ queryKey: calendarKeys.all }),
        queryClient.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'content-ref',
        }),
      ]);
    },
  });
};

export const useUpdateMessageDraft = () => {
  return useMutation({
    mutationFn: ({
      draftContentId,
      input,
    }: {
      draftContentId: number;
      input: UpdateMessageDraftInput;
    }) => updateMessageDraft(draftContentId, input),
  });
};

export const useCreateMessageReplyDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceContentId, input }: { sourceContentId: number; input: CreateMessageReplyDraftInput }) =>
      createMessageReplyDraft(sourceContentId, input),
    onSuccess: async (result, { sourceContentId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.detail(sourceContentId) }),
        queryClient.invalidateQueries({ queryKey: contentKeys.detail(result.draftContentId) }),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
      ]);
    },
  });
};

export const useDiscardMessageDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftContentId: number) => discardMessageDraft(draftContentId),
    onSuccess: async (result) => {
      queryClient.removeQueries({ queryKey: contentKeys.detail(result.discardedContentId) });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
        queryClient.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'content-ref',
        }),
      ]);
    },
  });
};

export const useSendMessageDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ draftContentId }: { draftContentId: number; refreshContentId: number }) =>
      sendMessageDraft(draftContentId),
    onSuccess: async (_, { refreshContentId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.detail(refreshContentId) }),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
        queryClient.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'content-ref',
        }),
      ]);
    },
  });
};

export const useCreateCalendarEventFromEventContext = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceContentId,
      input,
    }: {
      sourceContentId: number;
      input: Parameters<typeof createCalendarEventFromEventContext>[1];
    }) => createCalendarEventFromEventContext(sourceContentId, input),
    onSuccess: async (_, { sourceContentId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.detail(sourceContentId) }),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
      ]);
    },
  });
};

export const useUpdateCalendarEventFromEventContext = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceContentId,
      input,
    }: {
      sourceContentId: number;
      input: Parameters<typeof updateCalendarEventFromEventContext>[1];
    }) => updateCalendarEventFromEventContext(sourceContentId, input),
    onSuccess: async (_, { sourceContentId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.detail(sourceContentId) }),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
      ]);
    },
  });
};

// ============================================================================
// Tracking Mutation Hooks
// Tracking is embedded in Content structure, so we invalidate detail cache
// ============================================================================

/**
 * Mark Content as Read Mutation Hook
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAsRead,
    onSuccess: async (_, contentId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.detail(contentId) }),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
        queryClient.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'content-ref',
        }),
      ]);
    },
  });
};

/**
 * Toggle Star on Content Mutation Hook
 */
export const useToggleStar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contentId, isStarred }: { contentId: number; isStarred: boolean }) =>
      toggleStar(contentId, isStarred),
    onSuccess: (_, { contentId }) => {
      // Invalidate content detail to refetch with updated tracking
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(contentId) });
    },
  });
};

/**
 * Toggle Read State on Content Mutation Hook
 */
export const useToggleRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contentId, isRead }: { contentId: number; isRead: boolean }) =>
      setReadState(contentId, isRead),
    onSuccess: (_, { contentId }) => {
      queryClient.invalidateQueries({ queryKey: contentKeys.detail(contentId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['content-attachments'] });
    },
  });
};


